// tenants/UpdateDetails.js
import React, { useState, useEffect } from 'react';
import { Edit3, CheckCircle, Home, AlertTriangle, ArrowRight, Plus, Trash2, Phone } from 'lucide-react';
import { useAppTheme } from '../../contexts/ThemeContext';
import { fetchApartments, fetchCategories, fetchUnits, patchTenant } from './api';

// ── Shell ─────────────────────────────────────────────────────────────────────
const Shell = ({ theme: { bdr, bg, txt, P }, tenant, children, footer }) => (
  <div style={{ display: 'flex', flexDirection: 'column', height: '92vh', maxHeight: 860, overflow: 'hidden' }}>
    <div style={{
      flexShrink: 0, display: 'flex', alignItems: 'center', gap: 14,
      padding: '20px 28px', borderBottom: `1px solid ${bdr}`,
      backgroundColor: bg.primary,
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: `${P}18`,
      }}>
        <Edit3 size={20} style={{ color: P }} />
      </div>
      <div>
        <p style={{ fontWeight: 700, fontSize: 17, color: txt.primary, margin: 0, lineHeight: 1.2 }}>
          Update Tenant Details
        </p>
        <p style={{ fontSize: 13, color: txt.secondary, margin: '3px 0 0' }}>
          {tenant.name} · Unit {tenant.houseNumber} · {tenant.propertyName}
        </p>
      </div>
    </div>

    <div style={{
      flex: 1, overflowY: 'auto', padding: '24px 28px',
      display: 'flex', flexDirection: 'column', gap: 28,
    }}>
      {children}
    </div>

    <div style={{
      flexShrink: 0, display: 'flex', gap: 10,
      padding: '16px 28px', borderTop: `1px solid ${bdr}`,
      backgroundColor: bg.primary,
    }}>
      {footer}
    </div>
  </div>
);


// ── Main component ────────────────────────────────────────────────────────────
const UpdateDetails = ({ tenant, onClose, onUpdate }) => {
  const { palette, Colors } = useAppTheme();

  const bg   = palette.background;
  const txt  = palette.text;
  const bdr  = palette.border;
  const P    = Colors.primary.main;
  const ERR  = Colors.status.error;
  const WARN = Colors.status?.warning ?? '#F59E0B';

  const theme = { bdr, bg, txt, P, ERR, WARN };

  // ── Personal fields ───────────────────────────────────────────────────────
  const [firstName0, ...rest0] = (tenant.name || '').split(' ');
  const [form, setForm] = useState({
    firstName: tenant.firstName || firstName0 || '',
    lastName:  tenant.lastName  || rest0.join(' ') || '',
    idNumber:  tenant.idNumber  || '',
    phone:     tenant.phone     || '',
    email:     tenant.email     || '',
  });
  console.log('additionalPhones from prop:', tenant.additionalPhones);
  // ── Additional phones ─────────────────────────────────────────────────────
  const [additionalPhones, setAdditionalPhones] = useState(
    () => (tenant.additionalPhones || []).map(p => ({
      id:          p.id,
      phone:       p.phone || '',
      description: p.description || '',
    }))
  );

  const addPhone = () =>
    setAdditionalPhones(prev => [...prev, { phone: '', description: '' }]);

  const removePhone = (index) =>
    setAdditionalPhones(prev => prev.filter((_, i) => i !== index));

  const updatePhone = (index, field, value) =>
    setAdditionalPhones(prev =>
      prev.map((entry, i) => i === index ? { ...entry, [field]: value } : entry)
    );

  // ── Unit migration ────────────────────────────────────────────────────────
  const [propertyId,     setPropertyId]     = useState('');
  const [categoryId,     setCategoryId]     = useState('');
  const [unitId,         setUnitId]         = useState('');
  const [oldUnitRelease, setOldUnitRelease] = useState('');

  const [apartments, setApartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units,      setUnits]      = useState([]);

  // ── UI ────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const isMigrating = !!unitId;

  useEffect(() => {
    fetchApartments().then(setApartments).catch(() => {});
  }, []);

  const handlePropertyChange = async (v) => {
    setPropertyId(v); setCategoryId(''); setUnitId('');
    setCategories([]); setUnits([]);
    if (v) fetchCategories(v).then(setCategories).catch(() => {});
  };

  const handleCategoryChange = async (v) => {
    setCategoryId(v); setUnitId('');
    setUnits([]);
    if (v) fetchUnits(v).then(setUnits).catch(() => {});
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First name and last name are required.'); return;
    }
    if (isMigrating && !oldUnitRelease) {
      setError('Please choose how to release the current unit before saving.'); return;
    }
    // Validate additional phones — phone field must not be empty if a row exists
    for (const [i, p] of additionalPhones.entries()) {
      if (!p.phone.trim()) {
        setError(`Phone number in row ${i + 1} of additional contacts cannot be empty.`); return;
      }
    }

    setLoading(true); setError(null);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName:  form.lastName.trim(),
        idNumber:  form.idNumber.trim(),
        phone:     form.phone.trim(),
        email:     form.email.trim(),
        // Always send the full list so the backend can do a clean replace.
        additionalPhones: additionalPhones.map(p => ({
          ...(p.id ? { id: p.id } : {}),
          phone:       p.phone.trim(),
          description: p.description.trim(),
        })),
      };
      if (isMigrating) {
        payload.unitId         = Number(unitId);
        payload.oldUnitRelease = oldUnitRelease;
      }
      await patchTenant(tenant.id, payload);
      if (onUpdate) onUpdate();
      onClose();
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Shared styles ─────────────────────────────────────────────────────────
  const inp = {
    width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 14,
    outline: 'none', boxSizing: 'border-box',
    backgroundColor: bg.tertiary, border: `1px solid ${bdr}`, color: txt.primary,
  };

  const Label = ({ children }) => (
    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em',
      textTransform: 'uppercase', color: txt.secondary, marginBottom: 7 }}>
      {children}
    </label>
  );

  const SectionTitle = ({ children, sub }) => (
    <div style={{ marginBottom: 18 }}>
      <p style={{ fontWeight: 700, fontSize: 14, color: txt.primary, margin: 0 }}>{children}</p>
      {sub && <p style={{ fontSize: 12, color: txt.muted, margin: '3px 0 0' }}>{sub}</p>}
    </div>
  );

  // ── Footer ────────────────────────────────────────────────────────────────
  const footer = (
    <>
      <button onClick={onClose} disabled={loading} style={{
        flex: 1, padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600,
        cursor: loading ? 'not-allowed' : 'pointer',
        backgroundColor: bg.tertiary, color: txt.primary, border: `1px solid ${bdr}`,
      }}>
        Cancel
      </button>
      <button onClick={handleSave} disabled={loading} style={{
        flex: 2, padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600,
        cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.75 : 1,
        backgroundColor: P, color: '#fff', border: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      }}>
        {loading ? 'Saving…' : <><span>Save Changes</span><ArrowRight size={15} /></>}
      </button>
    </>
  );

  return (
    <Shell theme={theme} tenant={tenant} footer={footer}>

      {/* ── Error banner ─────────────────────────────────────────────────── */}
      {error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
          borderRadius: 10, backgroundColor: `${ERR}12`, border: `1px solid ${ERR}35`,
          color: ERR, fontSize: 13, marginBottom: -12,
        }}>
          <AlertTriangle size={15} style={{ flexShrink: 0 }} />
          {error}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* SECTION 1 — Personal Details                                      */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <div>
        <SectionTitle>Personal Details</SectionTitle>

        {/* Row 1 — First name + Last name */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <Label>First Name</Label>
            <input
              type="text"
              value={form.firstName}
              onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
              style={inp}
            />
          </div>
          <div>
            <Label>Last Name</Label>
            <input
              type="text"
              value={form.lastName}
              onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
              style={inp}
            />
          </div>
        </div>

        {/* Row 2 — ID Number */}
        <div style={{ marginBottom: 16 }}>
          <Label>ID / Passport Number</Label>
          <input
            type="text"
            value={form.idNumber}
            onChange={e => setForm(f => ({ ...f, idNumber: e.target.value }))}
            style={inp}
          />
        </div>

        {/* Row 3 — Primary Phone + Email */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <Label>Primary Phone</Label>
            <input
              type="tel"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              style={inp}
            />
          </div>
          <div>
            <Label>Email Address</Label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              style={inp}
            />
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* SECTION 2 — Additional Phone Numbers                             */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <div style={{ paddingTop: 24, borderTop: `1px solid ${bdr}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <SectionTitle
            sub="Extra contacts for this tenant e.g. roommate, spouse, work"
          >
            Additional Phone Numbers{' '}
            <span style={{ fontWeight: 400, fontSize: 12, color: txt.muted }}>(optional)</span>
          </SectionTitle>

          <button
            type="button"
            onClick={addPhone}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', flexShrink: 0, marginTop: -14,
              backgroundColor: `${P}15`, color: P, border: `1px solid ${P}40`,
            }}
          >
            <Plus size={14} />
            Add Phone
          </button>
        </div>

        {additionalPhones.length === 0 ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '14px 16px', borderRadius: 10,
            backgroundColor: bg.tertiary, border: `1px dashed ${bdr}`,
            color: txt.muted, fontSize: 13,
          }}>
            <Phone size={15} style={{ flexShrink: 0 }} />
            No additional phone numbers yet. Click "Add Phone" to add one.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {additionalPhones.map((entry, index) => (
              <div
                key={index}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10,
                  alignItems: 'flex-end',
                  padding: '14px 16px', borderRadius: 10,
                  backgroundColor: bg.tertiary, border: `1px solid ${bdr}`,
                }}
              >
                <div>
                  <Label>Phone Number</Label>
                  <input
                    type="tel"
                    placeholder="254712345678"
                    value={entry.phone}
                    onChange={e => updatePhone(index, 'phone', e.target.value)}
                    style={inp}
                  />
                </div>
                <div>
                  <Label>Label / Description</Label>
                  <input
                    type="text"
                    placeholder="e.g. Roommate, Spouse, Nickname, Work"
                    value={entry.description}
                    onChange={e => updatePhone(index, 'description', e.target.value)}
                    style={inp}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removePhone(index)}
                  title="Remove"
                  style={{
                    width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', marginBottom: 1,
                    backgroundColor: `${ERR}12`, color: ERR,
                    border: `1px solid ${ERR}30`,
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* SECTION 3 — Unit Migration (optional)                            */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <div style={{ paddingTop: 24, borderTop: `1px solid ${bdr}` }}>
        <SectionTitle
          sub={`Currently in Unit ${tenant.houseNumber} · ${tenant.propertyName}`}
        >
          Transfer Unit{' '}
          <span style={{ fontWeight: 400, fontSize: 12, color: txt.muted }}>(optional)</span>
        </SectionTitle>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
          <div>
            <Label>New Property</Label>
            <select value={propertyId} onChange={e => handlePropertyChange(e.target.value)} style={inp}>
              <option value="">Keep current property</option>
              {apartments.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          {propertyId && (
            <div>
              <Label>Category</Label>
              <select value={categoryId} onChange={e => handleCategoryChange(e.target.value)} style={inp}>
                <option value="">Select category</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {categoryId && (
            <div>
              <Label>Unit</Label>
              <select value={unitId} onChange={e => setUnitId(e.target.value)} style={inp}>
                <option value="">Select unit</option>
                {units.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.number || u.houseNumber}
                    {u.status && u.status !== 'Vacant' ? ` (${u.status})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {isMigrating && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: txt.secondary, margin: 0 }}>
                Release Unit {tenant.houseNumber} As
              </p>
              {!oldUnitRelease && (
                <span style={{ fontSize: 11, color: WARN, fontWeight: 500 }}>
                  ← required to proceed
                </span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                {
                  value: 'vacant_available',
                  label: 'Vacant & Available',
                  Icon:  CheckCircle,
                  desc:  'Ready for new tenant bookings immediately',
                },
                {
                  value: 'vacant_unavailable',
                  label: 'Vacant (Unavailable)',
                  Icon:  Home,
                  desc:  'Under maintenance or temporarily on hold',
                },
              ].map(({ value, label: lbl, Icon, desc }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setOldUnitRelease(value)}
                  style={{
                    padding: '14px 16px', borderRadius: 14, textAlign: 'left', cursor: 'pointer',
                    backgroundColor: oldUnitRelease === value ? `${P}15` : bg.tertiary,
                    border: `2px solid ${oldUnitRelease === value ? P : bdr}`,
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
                    color: oldUnitRelease === value ? P : txt.secondary,
                  }}>
                    <Icon size={15} />
                    <span style={{ fontSize: 12, fontWeight: 700 }}>{lbl}</span>
                  </div>
                  <p style={{ fontSize: 11, color: txt.muted, margin: 0 }}>{desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

    </Shell>
  );
};

export default UpdateDetails;